import { ReportParameterFilter } from 'src/components/star-grid/components/ReportParameterFilter';
import { CustomReportValues } from 'src/components/star-grid/components/CustomReportValues';
import { ReportHierarchyParameter } from 'src/components/star-grid/components/ReportHierarchyParameter';
import React, { useCallback, useState } from 'react';
import FormField from '@amzn/awsui-components-core/form-field';
import Select from '@amzn/awsui-components-core/select';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import Button from '@amzn/awsui-components-core/button';
import type { ReportParameter } from 'src/components/star-grid/reportDefinition.types';
import type { PnlHierarchyTree } from 'src/components/star-grid/starGridTypes';
import { useReportParameter } from 'src/components/star-grid/hooks/useReportParameter';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';

export interface ReportParameterFieldProps {
  readonly parameter: ReportParameter;
  readonly editable?: boolean;
  readonly compact?: boolean;
  readonly values: readonly string[];
  readonly error?: string;
  readonly disabled?: boolean;
  readonly onChange: (key: string, values: readonly string[]) => void;
  /**
   * How to fetch the member tree. Defaults to the by-`hierarchy_id` route; report filters inject the
   * by-pair route because most of their (dimension, hierarchy) pairs have no registered id.
   */
  readonly loadHierarchy?: (id: string, signal: AbortSignal) => Promise<PnlHierarchyTree | undefined>;
}
export const ReportParameterField: React.FC<ReportParameterFieldProps> = ({
  parameter,
  values,
  error,
  disabled,
  editable,
  compact,
  onChange,
  loadHierarchy
}) => {
  // In the report bar a filter's tree loads on first open: most are never opened, and some are large.
  const [treeWanted, setTreeWanted] = useState(!compact);
  const wantTree = useCallback(() => setTreeWanted(true), []);
  const state = useReportParameter(parameter, values, treeWanted, loadHierarchy);
  const text = useStarGridText();
  if (compact)
    return (
      <ReportParameterFilter
        parameter={parameter}
        values={values}
        error={error}
        disabled={disabled}
        onChange={onChange}
        state={state}
        treeWanted={treeWanted}
        onWantTree={wantTree}
      />
    );
  return (
    <FormField
      label={parameter.label}
      errorText={error ?? state.error?.message}
      description={
        compact
          ? undefined
          : parameter.role === 'row_root'
          ? text('rowRootHelp')
          : state.usesDefault
          ? text(editable ? (parameter.allow_custom_values ? 'templateCustomValuesHelp' : 'templateMissingChoices') : 'parameterDefault')
          : undefined
      }
      secondaryControl={
        state.error ? (
          <Button data-testid={`defined-options-retry-${parameter.key}`} formAction="none" onClick={state.reload}>
            {text('retry')}
          </Button>
        ) : !parameter.required && values.length > 0 && (!state.usesDefault || editable) ? (
          <Button
            data-testid={`defined-reset-${parameter.key}`}
            formAction="none"
            disabled={disabled}
            onClick={() => onChange(parameter.key, editable && parameter.default.length ? parameter.default : [])}
          >
            {text(parameter.default.length ? 'resetSelection' : 'clearSelection')}
          </Button>
        ) : undefined
      }
    >
      {editable && parameter.allow_custom_values && !parameter.hierarchy_id ? (
        <CustomReportValues
          values={values}
          multiple={parameter.multiple}
          disabled={disabled}
          testId={`defined-parameter-${parameter.key}`}
          onChange={(selected) => onChange(parameter.key, selected)}
        />
      ) : parameter.hierarchy_id ? (
        <ReportHierarchyParameter parameter={parameter} values={values} state={state} disabled={disabled} compact={compact} onChange={onChange} />
      ) : parameter.multiple ? (
        <Multiselect
          data-testid={`defined-parameter-${parameter.key}`}
          filteringType="auto"
          selectedOptions={state.selected}
          options={state.data ?? []}
          statusType={state.loading ? 'loading' : state.error ? 'error' : 'finished'}
          disabled={disabled || (state.usesDefault && !editable)}
          placeholder={text('selectValues')}
          loadingText={text('loadingOptions')}
          empty={text('noOptions')}
          onChange={({ detail }) =>
            onChange(
              parameter.key,
              detail.selectedOptions.flatMap((item) => (item.value ? [item.value] : []))
            )
          }
        />
      ) : (
        <Select
          data-testid={`defined-parameter-${parameter.key}`}
          filteringType="auto"
          selectedOption={state.selected[0] ?? null}
          options={state.data ?? []}
          statusType={state.loading ? 'loading' : state.error ? 'error' : 'finished'}
          disabled={disabled || (state.usesDefault && !editable)}
          placeholder={state.usesDefault ? text('usesDefault') : text('selectValue')}
          loadingText={text('loadingOptions')}
          empty={text('noOptions')}
          onChange={({ detail }) => onChange(parameter.key, detail.selectedOption.value ? [detail.selectedOption.value] : [])}
        />
      )}
    </FormField>
  );
};
